import { Alert, Button, Group, Text } from '@mantine/core';
import { IconListDetails, IconSend } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useShell } from '../../shell-context.js';
import type { SetupSummary } from './types.js';

/**
 * Etkin gemi (ADR 0142 §3, Ek 2026-10-31): ofis PMS'i kurulumda da canlıda da düzenler, değişiklik
 * gemiye gider ve geminin sonraki senkronunda uygulanır. Bekleyen ve gemide uygulanamayan değişiklik sayısı gösterilir;
 * `listPath` verilirse "View changes" listeyi (Fleet ▸ Vessels ▸ Setup Changes) açar. Yalnız
 * ofiste ve `queued` kipte çizilir.
 */
export function SetupChangesBanner({
  setup,
  listPath,
}: {
  setup: SetupSummary | undefined;
  listPath?: string | undefined;
}) {
  const { t } = useTranslation();
  const shell = useShell();
  if (setup?.mode !== 'queued') return null;
  const live = setup.state === 'live';
  return (
    <Alert
      color={setup.rejected > 0 ? 'orange' : 'blue'}
      variant="light"
      icon={<IconSend size={18} />}
      title={t(live ? 'pms.setupChanges.titleLive' : 'pms.setupChanges.title')}
      data-setup-changes
    >
      <Text fz="sm">{t(live ? 'pms.setupChanges.textLive' : 'pms.setupChanges.text')}</Text>
      {setup.pending > 0 || setup.rejected > 0 || (listPath && shell) ? (
        <Group gap="md" mt={4}>
          {setup.pending > 0 ? (
            <Text fz="sm" fw={500}>
              {t('pms.setupChanges.pending', { count: setup.pending })}
            </Text>
          ) : null}
          {setup.rejected > 0 ? (
            <Text fz="sm" fw={500} c="orange">
              {t('pms.setupChanges.rejected', { count: setup.rejected })}
            </Text>
          ) : null}
          {listPath && shell ? (
            <Button
              size="compact-sm"
              variant="subtle"
              leftSection={<IconListDetails size={15} />}
              onClick={() => shell.navigate(listPath)}
            >
              {t('pms.setupChangeList.open')}
            </Button>
          ) : null}
        </Group>
      ) : null}
    </Alert>
  );
}
